import { useEffect, useState } from "react";
import { Trophy, Sparkles, Sun, GraduationCap, Trash2, Eye, PartyPopper, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const KIND_META = {
  biweekly: { label: "Bi-Weekly", icon: Sparkles, color: "bg-blue-600", accent: "text-blue-700", ring: "ring-blue-200" },
  summer: { label: "Whole Summer", icon: Sun, color: "bg-amber-600", accent: "text-amber-700", ring: "ring-amber-200" },
  orientation: { label: "Orientation", icon: GraduationCap, color: "bg-violet-600", accent: "text-violet-700", ring: "ring-violet-200" },
};

function WinnersModal({ open, onClose, result }) {
  if (!open || !result) return null;
  const kind = result.kind || "biweekly";
  const meta = KIND_META[kind] || KIND_META.biweekly;
  const winners = result.winners || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="raffle-winners-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className={`${meta.color} text-white px-6 py-5 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <PartyPopper className="w-6 h-6" />
            <div>
              <div className="font-display font-bold text-xl leading-tight">{result.prize_label || meta.label + " Raffle"}</div>
              <div className="text-white/80 text-xs font-mono uppercase tracking-[0.18em]">{meta.label} draw · {winners.length} of {result.pool_size || 0} eligible</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="close-winners-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          {winners.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Trophy className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <div className="font-semibold">No eligible staff with perfect attendance.</div>
              <div className="text-sm">Check the date range or wait for more sign-ins.</div>
            </div>
          ) : (
            <ol className="space-y-2" data-testid="raffle-winners-list">
              {winners.map((w, i) => (
                <li key={w.crew_id} className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-slate-50 to-white border border-slate-200" data-testid={`winner-row-${i}`}>
                  <div className={`w-9 h-9 rounded-full ${meta.color} text-white flex items-center justify-center font-bold text-sm`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{w.name || "—"}</div>
                    <div className="text-xs text-slate-500 font-mono">{w.phone || ""}{w.division ? ` · ${w.division}` : ""}</div>
                  </div>
                  <Trophy className={`w-5 h-5 ${meta.accent}`} />
                </li>
              ))}
            </ol>
          )}
          <div className="mt-5 flex justify-end">
            <button onClick={onClose} className="btn-touch px-4 rounded-xl bg-slate-900 text-white font-semibold text-sm hover:bg-slate-800" data-testid="dismiss-winners-modal">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PoolPreviewPanel({ kind, query, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await api.get("/raffle/preview", { params: { kind, ...query } });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Could not preview pool");
      } finally { if (!cancelled) setLoading(false); }
    };
    run();
    return () => { cancelled = true; };
  }, [kind, query?.start_date, query?.end_date]);
  const meta = KIND_META[kind] || KIND_META.biweekly;
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`pool-preview-${kind}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-xs uppercase tracking-[0.16em] font-bold ${meta.accent}`}>Eligible pool · {loading ? "…" : (data?.pool_size ?? 0)} {data?.pool_size === 1 ? "person" : "people"}</div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
      </div>
      {loading && <div className="text-sm text-slate-500">Calculating…</div>}
      {!loading && data && (
        <>
          {data.qualifying_dates?.length > 0 && (
            <div className="text-[11px] text-slate-500 mb-2 font-mono">{data.qualifying_dates.length} qualifying day{data.qualifying_dates.length === 1 ? "" : "s"}: {data.qualifying_dates.join(", ")}</div>
          )}
          {data.pool?.length > 0 ? (
            <div className="max-h-40 overflow-y-auto divide-y divide-slate-200 bg-white rounded-lg border border-slate-200">
              {data.pool.map((p) => (
                <div key={p.crew_id} className="px-3 py-1.5 text-sm flex justify-between" data-testid={`pool-row-${p.crew_id}`}>
                  <span className="font-semibold text-slate-800">{p.name}</span>
                  <span className="text-slate-500 text-xs font-mono">{p.division || ""}</span>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-slate-500">No one has perfect attendance for this window yet.</div>}
        </>
      )}
    </div>
  );
}

function RaffleCard({ kind, title, description, icon: Icon, onDraw, children, busy }) {
  const meta = KIND_META[kind];
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 ring-1 ${meta.ring}`} data-testid={`raffle-card-${kind}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${meta.color} text-white flex items-center justify-center shrink-0`}><Icon className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="font-display font-bold text-base leading-tight">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{description}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function RafflesPanel() {
  // biweekly state
  const [bwStart, setBwStart] = useState("");
  const [bwEnd, setBwEnd] = useState("");
  const [bwCount, setBwCount] = useState(1);
  const [bwPrize, setBwPrize] = useState("");
  const [bwPreviewOpen, setBwPreviewOpen] = useState(false);

  // summer state
  const [smCount, setSmCount] = useState(1);
  const [smPrize, setSmPrize] = useState("");
  const [smPreviewOpen, setSmPreviewOpen] = useState(false);

  // orientation state
  const [orCount, setOrCount] = useState(1);
  const [orPrize, setOrPrize] = useState("");
  const [orPreviewOpen, setOrPreviewOpen] = useState(false);

  const [busy, setBusy] = useState(false);
  const [modalResult, setModalResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const res = await api.get("/raffle/history", { params: { limit: 50 } });
      setHistory(res.data.items || []);
    } catch (e) { /* non-fatal */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const runDraw = async (url, payload) => {
    setBusy(true);
    try {
      const res = await api.post(url, payload);
      setModalResult(res.data);
      if ((res.data.winners || []).length > 0) {
        toast.success(`${res.data.winners.length} winner${res.data.winners.length === 1 ? "" : "s"} drawn`);
      } else {
        toast.message("No one qualified for this raffle.");
      }
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draw failed");
    } finally { setBusy(false); }
  };

  const drawBiweekly = () => {
    if (!bwStart || !bwEnd) return toast.error("Pick a start and end date");
    runDraw("/raffle/biweekly", {
      start_date: bwStart, end_date: bwEnd,
      winner_count: Math.max(1, Number(bwCount) || 1),
      prize_label: bwPrize || "",
    });
  };
  const drawSummer = () => runDraw("/raffle/summer", {
    winner_count: Math.max(1, Number(smCount) || 1), prize_label: smPrize || "",
  });
  const drawOrientation = () => runDraw("/raffle/orientation", {
    winner_count: Math.max(1, Number(orCount) || 1), prize_label: orPrize || "",
  });

  const deleteDraw = async (id) => {
    if (!window.confirm("Delete this raffle draw from history?")) return;
    try {
      await api.delete(`/raffle/history/${id}`);
      loadHistory();
      toast.success("Draw deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="raffles-section">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="w-4 h-4 text-amber-600" />
        <h2 className="font-display text-lg font-bold">Perfect Attendance Raffles</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">Draw winners from staff who were <span className="font-semibold">Present</span> on every qualifying day. Source: crew sign-ins.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* BIWEEKLY */}
        <RaffleCard kind="biweekly" title="Bi-Weekly Raffle" description="Pick any date range — weekends and 7/3 are auto-excluded." icon={Sparkles}>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Start</span>
              <input type="date" value={bwStart} onChange={(e) => setBwStart(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" data-testid="bw-start" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">End</span>
              <input type="date" value={bwEnd} onChange={(e) => setBwEnd(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" data-testid="bw-end" />
            </label>
          </div>
          <label className="flex flex-col gap-1 mt-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Winners</span>
            <input type="number" min="1" value={bwCount} onChange={(e) => setBwCount(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" data-testid="bw-count" />
          </label>
          <label className="flex flex-col gap-1 mt-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Prize label <span className="text-slate-400 normal-case">(optional)</span></span>
            <input type="text" value={bwPrize} onChange={(e) => setBwPrize(e.target.value)} placeholder="e.g. Bagels &amp; coffee" className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" data-testid="bw-prize" />
          </label>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setBwPreviewOpen((v) => !v)} disabled={!bwStart || !bwEnd} className="btn-touch flex-1 inline-flex items-center justify-center gap-1 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-40" data-testid="bw-preview-btn"><Eye className="w-4 h-4" /> Preview</button>
            <button onClick={drawBiweekly} disabled={busy || !bwStart || !bwEnd} className="btn-touch flex-1 inline-flex items-center justify-center gap-1 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold disabled:opacity-50" data-testid="bw-draw-btn"><Trophy className="w-4 h-4" /> Draw</button>
          </div>
          {bwPreviewOpen && bwStart && bwEnd && (
            <PoolPreviewPanel kind="biweekly" query={{ start_date: bwStart, end_date: bwEnd }} onClose={() => setBwPreviewOpen(false)} />
          )}
        </RaffleCard>

        {/* SUMMER */}
        <RaffleCard kind="summer" title="Whole Summer Raffle" description="6/29/26 – 8/20/26, weekdays only, excluding 7/3/26." icon={Sun}>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Winners</span>
            <input type="number" min="1" value={smCount} onChange={(e) => setSmCount(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" data-testid="sm-count" />
          </label>
          <label className="flex flex-col gap-1 mt-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Prize label <span className="text-slate-400 normal-case">(optional)</span></span>
            <input type="text" value={smPrize} onChange={(e) => setSmPrize(e.target.value)} placeholder="e.g. Grand prize gift card" className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" data-testid="sm-prize" />
          </label>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setSmPreviewOpen((v) => !v)} className="btn-touch flex-1 inline-flex items-center justify-center gap-1 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50" data-testid="sm-preview-btn"><Eye className="w-4 h-4" /> Preview</button>
            <button onClick={drawSummer} disabled={busy} className="btn-touch flex-1 inline-flex items-center justify-center gap-1 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold disabled:opacity-50" data-testid="sm-draw-btn"><Trophy className="w-4 h-4" /> Draw</button>
          </div>
          {smPreviewOpen && (
            <PoolPreviewPanel kind="summer" query={{}} onClose={() => setSmPreviewOpen(false)} />
          )}
        </RaffleCard>

        {/* ORIENTATION */}
        <RaffleCard kind="orientation" title="Orientation Raffle" description="Perfect attendance on 6/6, 6/13, 6/20 + one of 6/16 or 6/17." icon={GraduationCap}>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Winners</span>
            <input type="number" min="1" value={orCount} onChange={(e) => setOrCount(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" data-testid="or-count" />
          </label>
          <label className="flex flex-col gap-1 mt-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Prize label <span className="text-slate-400 normal-case">(optional)</span></span>
            <input type="text" value={orPrize} onChange={(e) => setOrPrize(e.target.value)} placeholder="e.g. Mateys kickoff prize" className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" data-testid="or-prize" />
          </label>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setOrPreviewOpen((v) => !v)} className="btn-touch flex-1 inline-flex items-center justify-center gap-1 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50" data-testid="or-preview-btn"><Eye className="w-4 h-4" /> Preview</button>
            <button onClick={drawOrientation} disabled={busy} className="btn-touch flex-1 inline-flex items-center justify-center gap-1 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold disabled:opacity-50" data-testid="or-draw-btn"><Trophy className="w-4 h-4" /> Draw</button>
          </div>
          {orPreviewOpen && (
            <PoolPreviewPanel kind="orientation" query={{}} onClose={() => setOrPreviewOpen(false)} />
          )}
        </RaffleCard>
      </div>

      {/* History */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold text-sm uppercase tracking-[0.14em] text-slate-600">Raffle history</h3>
          <span className="text-xs text-slate-400">{history.length} draw{history.length === 1 ? "" : "s"}</span>
        </div>
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="raffle-history-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-500">Drawn</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-500">Kind</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-500">Prize</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-500">Range</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-500">Winners</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No draws yet.</td></tr>
              )}
              {history.map((h) => {
                const meta = KIND_META[h.kind] || KIND_META.biweekly;
                const winnersText = (h.winners || []).map((w) => w.name).join(", ") || "—";
                const range = h.kind === "orientation"
                  ? "Orientation dates"
                  : `${h.start_date || "—"} → ${h.end_date || "—"}`;
                return (
                  <tr key={h.id} className="border-t border-slate-100 align-top" data-testid={`history-row-${h.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap">{h.drawn_at_utc?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-[0.12em] text-white ${meta.color}`}>{meta.label}</span></td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{h.prize_label || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 font-mono">{range}</td>
                    <td className="px-3 py-2 text-slate-800">
                      <div className="text-xs text-slate-400">{(h.winners || []).length} of {h.pool_size} eligible</div>
                      <div>{winnersText}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => deleteDraw(h.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Delete draw" data-testid={`delete-draw-${h.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <WinnersModal open={!!modalResult} result={modalResult} onClose={() => setModalResult(null)} />
    </section>
  );
}
